/**
 * FirmaDualMatch – 5 Sub-Views als Tab-Content
 * ==============================================
 * Dashboard, Suchprofile, Alle Bewerber, Matches, Benachrichtigungen
 */
import { useState, useEffect, useMemo, useRef, useCallback, type ReactNode } from 'react'
import { assetUrl } from '@/utils/assetUrl'
import Avatar from '@/components/Avatar'
import {
  Phone, Mail, FileText, Star, X, MapPin, GraduationCap,
  Briefcase, Calendar, Building2, Languages, Sparkles, Award,
  LayoutDashboard, SlidersHorizontal, Users, Target, Bell, HelpCircle,
} from 'lucide-react'

const API = '/api/dualmatch'

// ── Types ─────────────────────────────────────────────────────

interface Lead {
  id: string; dynamicsId: string; dynamicsUrl: string;
  firstName?: string; lastName?: string; email?: string; telefon?: string;
  studiengang?: string; fachrichtung?: string; standort?: string;
  hochschulzentrum?: string; startzeitpunkt?: string; abschlussart?: string;
  interessen: string[]; beschaeftigungsmodell?: string; beruf?: string;
  erfahrungen?: string; sprachen?: string; importedAt: string;
  score?: number; hasFiles?: boolean; contactId?: string;
}
interface Match {
  id: string; score: number; status: string; matchedAt: string;
  lead: { firstName?: string; lastName?: string; email?: string; studiengang?: string; standort?: string; abschlussart?: string; dynamicsUrl: string };
  searchProfile: { name: string };
}
interface DashboardStats { activeProfiles: number; totalMatches: number; notificationsSent: number; recentMatches: Match[] }
interface SearchProfile { id: string; name: string; description?: string; isActive: boolean; createdAt: string; updatedAt: string; criteria: SearchCriteria[]; _count?: { matches: number } }
interface SearchCriteria { id?: string; field: string; operator: string; value: string }
interface Notification { id: string; channel: string; recipient: string; subject: string; status: string; sentAt?: string; createdAt: string; error?: string }

const FILTER_FIELDS = [
  { value: 'studiengang', label: 'Ausbildungsberuf / Jobtitel' },
  { value: 'fachrichtung', label: 'Gewünschte Branche' },
  { value: 'standort', label: 'Gewünschter Arbeitsort' },
  { value: 'hochschulzentrum', label: 'Hochschulzentrum' },
  { value: 'startzeitpunkt', label: 'Startzeitpunkt / Semester' },
  { value: 'abschlussart', label: 'Höchster Bildungsabschluss' },
  { value: 'interessen', label: 'Interessen' },
  { value: 'beschaeftigungsmodell', label: 'Beschäftigungsform' },
]
const OPERATORS = [
  { value: 'EQUALS', label: 'ist gleich' }, { value: 'NOT_EQUALS', label: 'ist nicht gleich' },
  { value: 'CONTAINS', label: 'enthält' }, { value: 'IN', label: 'ist einer von' },
  { value: 'GREATER_THAN', label: 'größer als' }, { value: 'LESS_THAN', label: 'kleiner als' },
  { value: 'BETWEEN', label: 'zwischen' },
]

// DualMatch-Favoriten: API-basiert (MariaDB)
let _dmFavsCache: Set<string> = new Set()
let _dmFavsLoaded = false

function useDualMatchFavorites() {
  const [favs, setFavs] = useState<Set<string>>(_dmFavsCache)
  const mounted = useRef(true)
  useEffect(() => { mounted.current = true; return () => { mounted.current = false } }, [])

  useEffect(() => {
    if (_dmFavsLoaded) return
    fetch('/api/user/preferences', { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data?.dualmatch_favorites && mounted.current) {
          const s = new Set<string>(data.dualmatch_favorites)
          _dmFavsCache = s
          setFavs(s)
        }
        _dmFavsLoaded = true
      })
      .catch(() => { _dmFavsLoaded = true })
  }, [])

  const toggle = useCallback((id: string) => {
    setFavs(prev => {
      const n = new Set(prev)
      if (n.has(id)) n.delete(id); else n.add(id)
      _dmFavsCache = n
      fetch('/api/user/preferences/dualmatch-favorites', {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify([...n]),
      }).catch(() => {})
      return n
    })
  }, [])

  return { favs, toggle }
}

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const res = await fetch(`${API}${path}`, { ...options, credentials: 'include', headers: { 'Content-Type': 'application/json', ...options.headers } })
  const data = await res.json()
  if (!res.ok) throw new Error(data.error || data.detail || 'Anfrage fehlgeschlagen')
  return data
}

// ═══════════════════════════════════════════════════════════════
// Hauptkomponente
// ═══════════════════════════════════════════════════════════════

type DualMatchView = 'dashboard' | 'profiles' | 'leads' | 'matches' | 'notifications'

const NAV_ITEMS: { id: DualMatchView; label: string; icon: ReactNode }[] = [
  { id: 'dashboard',     label: 'Dashboard',          icon: <LayoutDashboard size={16} /> },
  { id: 'profiles',      label: 'Suchprofile',        icon: <SlidersHorizontal size={16} /> },
  { id: 'leads',         label: 'Alle Talente',       icon: <Users size={16} /> },
  { id: 'matches',       label: 'Matches',            icon: <Target size={16} /> },
  { id: 'notifications', label: 'Benachrichtigungen', icon: <Bell size={16} /> },
]

export default function FirmaDualMatch() {
  const [view, setView] = useState<DualMatchView>('dashboard')

  return (
    <div style={{
      display: 'flex', flexDirection: 'column',
      minHeight: '100%', background: '#f7f7f7',
    }}>
      {/* ── Modern Top-Navigation ───────────────────────────── */}
      <DualMatchTopNav view={view} setView={setView} />

      {/* ── Inhalt ───────────────────────────────────────────── */}
      <div style={{ minWidth: 0, overflow: 'hidden', flex: 1 }}>
        {view === 'dashboard' && <DashboardView />}
        {view === 'profiles' && <SearchProfilesView />}
        {view === 'leads' && <LeadsView />}
        {view === 'matches' && <MatchesView />}
        {view === 'notifications' && <NotificationsView />}
      </div>
    </div>
  )
}

/* ── DualMatch Top-Navigation ─────────────────────────────────── */

function DualMatchTopNav({ view, setView }: { view: DualMatchView; setView: (v: DualMatchView) => void }) {
  const [helpOpen, setHelpOpen] = useState(false)
  return (
    <div style={{
      position: 'sticky', top: 0, zIndex: 40,
      background: 'rgba(255,255,255,0.92)',
      backdropFilter: 'saturate(180%) blur(14px)',
      WebkitBackdropFilter: 'saturate(180%) blur(14px)',
      borderBottom: '1px solid #ececec',
      boxShadow: '0 1px 0 rgba(0,0,0,0.02), 0 8px 24px -16px rgba(11,38,35,0.12)',
    }}>
      {helpOpen && <HowItWorksModal onClose={() => setHelpOpen(false)} />}
      <div style={{
        maxWidth: 1400, margin: '0 auto',
        padding: '14px 32px',
        display: 'flex', alignItems: 'center', gap: 24,
      }}>
        {/* Brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 10,
            background: 'linear-gradient(135deg, #009B8D, #006B5F)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 6px 16px rgba(0,155,141,0.28)',
          }}>
            <Sparkles size={18} color="white" />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.05 }}>
            <div style={{
              fontSize: 9, fontWeight: 700, color: '#009B8D',
              letterSpacing: '0.16em', textTransform: 'uppercase',
            }}>
              Praxispartner
            </div>
            <div style={{
              fontSize: 16, fontWeight: 900, color: '#0B2623',
              letterSpacing: '-0.02em',
              fontFamily: "'Neue-Haas-Grotesk-Display-Pro', Helvetica, Arial, sans-serif",
            }}>
              FOM DualMatch
            </div>
          </div>
        </div>

        {/* Trenner */}
        <div style={{ width: 1, height: 32, background: '#ececec', flexShrink: 0 }} />

        {/* Segmented Pill-Nav */}
        <nav style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: 4, borderRadius: 999,
          background: '#f4f5f4',
          border: '1px solid #ececec',
          flex: 1, minWidth: 0,
          overflowX: 'auto',
        }}>
          {NAV_ITEMS.map(item => {
            const active = view === item.id
            return (
              <button
                key={item.id}
                onClick={() => setView(item.id)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  padding: '9px 18px', borderRadius: 999, border: 'none',
                  fontSize: 13, fontWeight: 700,
                  cursor: 'pointer', whiteSpace: 'nowrap',
                  background: active ? 'white' : 'transparent',
                  color: active ? '#0B2623' : '#6b7280',
                  boxShadow: active ? '0 2px 8px rgba(11,38,35,0.10), 0 0 0 1px rgba(0,155,141,0.18)' : 'none',
                  transition: 'all .18s ease',
                }}
                onMouseEnter={(e) => {
                  if (!active) {
                    (e.currentTarget as HTMLButtonElement).style.color = '#0B2623'
                    ;(e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.6)'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!active) {
                    (e.currentTarget as HTMLButtonElement).style.color = '#6b7280'
                    ;(e.currentTarget as HTMLButtonElement).style.background = 'transparent'
                  }
                }}
              >
                <span style={{ color: active ? '#009B8D' : '#9ca3af', display: 'inline-flex' }}>
                  {item.icon}
                </span>
                {item.label}
              </button>
            )
          })}
        </nav>

        {/* Hilfe-Icon-Button → öffnet Modal mit "So funktioniert es" */}
        <button
          onClick={() => setHelpOpen(true)}
          aria-label="Hilfe – So funktioniert FOM DualMatch"
          title="So funktioniert FOM DualMatch"
          style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 38, height: 38, borderRadius: '50%',
            border: '1px solid #ececec', background: 'white',
            color: '#6b7280', cursor: 'pointer', flexShrink: 0,
            transition: 'all .15s',
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = '#009B8D'
            ;(e.currentTarget as HTMLButtonElement).style.borderColor = '#009B8D'
            ;(e.currentTarget as HTMLButtonElement).style.background = '#f0fdfb'
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.color = '#6b7280'
            ;(e.currentTarget as HTMLButtonElement).style.borderColor = '#ececec'
            ;(e.currentTarget as HTMLButtonElement).style.background = 'white'
          }}
        >
          <HelpCircle size={18} />
        </button>
      </div>
    </div>
  )
}

/* ── So-funktioniert-es Modal (über Hilfe-Icon erreichbar) ───── */

function HowItWorksModal({ onClose }: { onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  const steps = [
    { num: '01', title: 'Suchprofil anlegen', desc: 'Definieren Sie Studiengang, Standort und weitere Wunschkriterien für Ihre Talentsuche.' },
    { num: '02', title: 'Talente finden',     desc: 'Wir gleichen Ihr Profil mit interessierten Studieninteressierten ab — automatisch und kontinuierlich.' },
    { num: '03', title: 'Direkt kontaktieren', desc: 'Sie werden automatisch informiert und können Gespräche mit passenden Kandidaten starten.' },
  ]

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(11, 38, 35, 0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        zIndex: 1100,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        animation: 'dm-fade-in .18s ease-out',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 24,
          width: 'min(880px, 100%)', maxHeight: '90vh', overflow: 'auto',
          boxShadow: '0 32px 96px rgba(11,38,35,0.32)',
          animation: 'dm-pop-in .22s cubic-bezier(.2,.9,.3,1.2)',
        }}
      >
        {/* Header mit Verlauf */}
        <div style={{
          position: 'relative',
          padding: '32px 36px 24px',
          background: 'linear-gradient(135deg, #0B2623 0%, #006B5F 60%, #009B8D 100%)',
          color: 'white',
        }}>
          <button
            onClick={onClose}
            aria-label="Schließen"
            style={{
              position: 'absolute', top: 18, right: 18,
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.12)', border: 'none',
              color: 'white', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={18} />
          </button>
          <div style={{
            fontSize: 11, fontWeight: 700, opacity: 0.78,
            letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 6,
          }}>
            FOM DualMatch · Hilfe
          </div>
          <h2 style={{
            margin: 0, fontSize: 28, fontWeight: 900, letterSpacing: '-0.02em',
            fontFamily: "'Neue-Haas-Grotesk-Display-Pro', Helvetica, Arial, sans-serif",
          }}>
            So funktioniert es
          </h2>
          <p style={{ margin: '8px 0 0', fontSize: 14, opacity: 0.85, maxWidth: 540 }}>
            In drei Schritten zu passenden Talenten für Ihr duales Studienmodell.
          </p>
        </div>

        {/* Schritte */}
        <div style={{
          padding: '32px 36px',
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20,
        }}>
          {steps.map(step => (
            <div
              key={step.num}
              style={{
                background: '#fafbfa', borderRadius: 14, padding: '24px 26px',
                border: '1px solid #f0f0f0', position: 'relative', overflow: 'hidden',
              }}
            >
              <div style={{
                position: 'absolute', top: -18, right: -8,
                fontSize: 90, fontWeight: 900, color: '#f0fdfb',
                letterSpacing: '-0.04em', lineHeight: 1, userSelect: 'none',
                fontFamily: "'Neue-Haas-Grotesk-Display-Pro', Helvetica, Arial, sans-serif",
              }}>
                {step.num}
              </div>
              <div style={{ position: 'relative' }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: '#009B8D',
                  letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 8,
                }}>
                  Schritt {step.num}
                </div>
                <div style={{
                  fontSize: 17, fontWeight: 800, color: '#0B2623',
                  marginBottom: 8, letterSpacing: '-0.01em',
                }}>
                  {step.title}
                </div>
                <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                  {step.desc}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Footer mit Mehr-erfahren-Link */}
        <div style={{
          padding: '18px 36px 28px',
          borderTop: '1px solid #f0f0f0',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <a
            href="https://www.fom.de/de/fom-dualmatch.html"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontSize: 13, fontWeight: 700, color: '#009B8D',
              textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
            }}
          >
            Mehr über FOM DualMatch erfahren →
          </a>
          <button
            onClick={onClose}
            style={{
              padding: '10px 22px', borderRadius: 999,
              background: '#0B2623', color: 'white',
              border: 'none', fontSize: 13, fontWeight: 700, cursor: 'pointer',
            }}
          >
            Verstanden
          </button>
        </div>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Dashboard
// ═══════════════════════════════════════════════════════════════

function DashboardView() {
  const [stats, setStats] = useState<DashboardStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [syncMsg, setSyncMsg] = useState('')

  useEffect(() => {
    request<any>('/dashboard/stats').then(res => setStats(res.data)).catch(() => setStats({ activeProfiles: 0, totalMatches: 0, notificationsSent: 0, recentMatches: [] })).finally(() => setLoading(false))
  }, [])

  async function triggerSync() {
    setSyncing(true); setSyncMsg('Sync läuft...')
    try {
      const res = await request<any>('/sync/trigger', { method: 'POST' })
      setSyncMsg(res.message || 'Sync abgeschlossen')
      const s = await request<any>('/dashboard/stats'); setStats(s.data)
    } catch (e: any) { setSyncMsg(`Fehler: ${e.message}`) }
    finally { setSyncing(false); setTimeout(() => setSyncMsg(''), 5000) }
  }

  if (loading) return <Loading />
  if (!stats) return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Fehler beim Laden</div>

  const hasProfiles = stats.activeProfiles > 0
  const hasMatches = stats.totalMatches > 0

  return (
    <div style={{ margin: '-24px -24px 0', overflow: 'hidden' }}>
      {/* ══ Hero ══ */}
      <div style={{ position: 'relative', height: 380, overflow: 'hidden' }}>
        <img
          src={assetUrl('/FOM_Dual Match_Key-Visual_TeaserTile_B2B.png')}
          alt="FOM DualMatch"
          style={{
            position: 'absolute', inset: 0, width: '100%', height: '100%',
            objectFit: 'cover', objectPosition: 'center 30%',
          }}
        />
        {/* Verlauf von links damit Text auf Teal-Seite gut lesbar ist */}
        <div style={{
          position: 'absolute', inset: 0,
          background: 'linear-gradient(90deg, rgba(11,38,35,0.85) 0%, rgba(11,38,35,0.65) 35%, rgba(11,38,35,0.15) 65%, rgba(11,38,35,0) 100%)',
        }} />

        <div style={{
          position: 'relative', height: '100%', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', padding: '0 56px', maxWidth: 1280, margin: '0 auto',
        }}>
          <div style={{
            color: 'white', fontSize: 12, fontWeight: 700,
            letterSpacing: '0.22em', textTransform: 'uppercase',
            marginBottom: 18, opacity: 0.85,
            textShadow: '0 2px 12px rgba(0,0,0,0.35)',
          }}>
            FOM DualMatch · Exklusiv für Praxispartner
          </div>

          <h1 style={{
            margin: 0, color: 'white', fontSize: 56, fontWeight: 900,
            letterSpacing: '-0.025em', lineHeight: 1.02,
            textShadow: '0 4px 32px rgba(0,0,0,0.45)',
            fontFamily: "'Neue-Haas-Grotesk-Display-Pro', Helvetica, Arial, sans-serif",
            maxWidth: 600,
          }}>
            Talente, die zu Ihnen passen.
          </h1>

          <p style={{
            margin: '20px 0 0', maxWidth: 540,
            color: 'rgba(255,255,255,0.88)', fontSize: 17, lineHeight: 1.6,
            textShadow: '0 2px 12px rgba(0,0,0,0.35)',
          }}>
            Entdecken Sie qualifizierte Nachwuchskräfte für Ihr duales Studienmodell —
            vorgefiltert nach Ihren Wunschkriterien, persönlich ansprechbar.
          </p>
        </div>
      </div>

      {/* ══ Inhalt ══ */}
      <div style={{ maxWidth: 1280, margin: '0 auto', padding: '36px 56px 56px' }}>

        {/* Sync-Bar */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
          marginBottom: 28, gap: 16,
        }}>
          <button
            onClick={triggerSync}
            disabled={syncing}
            style={{
              padding: '9px 18px', borderRadius: 999,
              background: 'white', color: '#374151',
              border: '1px solid #e5e7eb',
              fontSize: 13, fontWeight: 600, cursor: syncing ? 'not-allowed' : 'pointer',
              opacity: syncing ? 0.6 : 1,
            }}
          >
            {syncing ? '⟳ Sync läuft...' : '⟳ Aktualisieren'}
          </button>
        </div>

        {syncMsg && (
          <div style={{
            padding: '12px 18px', background: '#f0fdf4', border: '1px solid #86efac',
            borderRadius: 10, marginBottom: 24, fontSize: 14, color: '#166534',
          }}>
            {syncMsg}
          </div>
        )}

        {/* Onboarding-Banner */}
        {!hasProfiles && (
          <div style={{
            padding: '24px 28px', marginBottom: 32, borderRadius: 16,
            background: 'linear-gradient(135deg, #ffffff 0%, #f0fdfb 100%)',
            border: '1px solid #ccf3ef',
            boxShadow: '0 2px 24px rgba(0,155,141,0.06)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
              <div style={{
                width: 56, height: 56, borderRadius: 16,
                background: 'linear-gradient(135deg, #00c6b2, #006B5F)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 26, flexShrink: 0,
                boxShadow: '0 8px 24px rgba(0,155,141,0.25)',
              }}>
                ✦
              </div>
              <div style={{ flex: 1 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: '#009B8D',
                  letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 4,
                }}>
                  Erste Schritte
                </div>
                <div style={{ fontSize: 17, fontWeight: 700, color: '#0B2623', marginBottom: 4 }}>
                  Starten Sie Ihre Talentsuche
                </div>
                <div style={{ fontSize: 14, color: '#5b7570', lineHeight: 1.6 }}>
                  Legen Sie Ihr erstes Suchprofil an — z.B. „Wirtschaftsinformatik Düsseldorf"
                  oder „Marketing Köln". Sobald wir passende Studieninteressierte für Sie finden,
                  erscheinen sie direkt hier in der Übersicht.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* KPIs */}
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 20, marginBottom: 36,
        }}>
          <DashStatCard
            label="Aktive Suchprofile"
            value={stats.activeProfiles}
            color="#009B8D"
            icon="🎯"
            hint="Ihre Wunschkriterien"
          />
          <DashStatCard
            label="Passende Talente"
            value={stats.totalMatches}
            color="#FF8F00"
            icon="✨"
            hint="Studierende die zu Ihnen passen"
          />
          <DashStatCard
            label="Benachrichtigungen"
            value={stats.notificationsSent}
            color="#5C6BC0"
            icon="📧"
            hint="Bereits an Sie versendet"
          />
        </div>

        {/* Aktuelle Talente */}
        <div style={{
          background: 'white', borderRadius: 16, border: '1px solid #e5e7eb',
          overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}>
          <div style={{
            padding: '22px 32px', borderBottom: '1px solid #f3f4f6',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            background: 'linear-gradient(180deg, #fafbfa 0%, white 100%)',
          }}>
            <div>
              <div style={{
                fontSize: 11, fontWeight: 700, color: '#009B8D',
                letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 4,
              }}>
                Live · Aktuelle Treffer
              </div>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0B2623', letterSpacing: '-0.02em' }}>
                Aktuelle Talente
              </h2>
              <div style={{ fontSize: 13, color: '#9ca3af', marginTop: 2 }}>
                Die {Math.min(stats.recentMatches.length, 10)} jüngsten Treffer aus allen aktiven Suchprofilen
              </div>
            </div>
            {hasMatches && (
              <span style={{
                fontSize: 12, fontWeight: 700, color: 'white',
                background: 'linear-gradient(135deg, #FF8F00, #f97316)',
                padding: '8px 16px', borderRadius: 999,
                boxShadow: '0 4px 12px rgba(249,115,22,0.25)',
              }}>
                Top {stats.recentMatches.length}
              </span>
            )}
          </div>

          {stats.recentMatches.length === 0 ? (
            <div style={{ padding: 80, textAlign: 'center' }}>
              <div style={{
                width: 64, height: 64, margin: '0 auto 16px',
                borderRadius: '50%', background: '#f9fafb',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, color: '#d1d5db',
              }}>
                ◌
              </div>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#374151', marginBottom: 6 }}>
                Noch keine passenden Talente
              </div>
              <div style={{ fontSize: 13, color: '#9ca3af', maxWidth: 380, margin: '0 auto' }}>
                {hasProfiles
                  ? 'Wir suchen weiter — sobald eine passende Person verfügbar ist, sehen Sie sie hier.'
                  : 'Erstellen Sie ein Suchprofil im Tab „Suchprofile", um loszulegen.'}
              </div>
            </div>
          ) : (
            <table style={tableStyle}>
              <thead><tr style={{ background: '#fafbfa' }}>
                <th style={thStyle}>Talent</th>
                <th style={thStyle}>Studiengang</th>
                <th style={thStyle}>Standort</th>
                <th style={thStyle}>Suchprofil</th>
                <th style={{ ...thStyle, textAlign: 'right' }}>Match</th>
                <th style={thStyle}>Datum</th>
              </tr></thead>
              <tbody>
                {stats.recentMatches.map(m => {
                  return (
                    <tr key={m.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={tdStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <Avatar
                            fallbackName={`${m.lead.firstName ?? ''} ${m.lead.lastName ?? ''}`.trim()}
                            size={36}
                          />
                          <strong style={{ color: '#0B2623' }}>
                            {m.lead.firstName} {m.lead.lastName}
                          </strong>
                        </div>
                      </td>
                      <td style={tdStyle}>{m.lead.studiengang || '–'}</td>
                      <td style={tdStyle}>{m.lead.standort || '–'}</td>
                      <td style={tdStyle}><Tag>{m.searchProfile.name}</Tag></td>
                      <td style={{ ...tdStyle, textAlign: 'right' }}>
                        <span style={{
                          display: 'inline-block', minWidth: 50,
                          padding: '4px 12px', borderRadius: 999,
                          fontSize: 12, fontWeight: 700,
                          background: m.score >= 0.8 ? '#dcfce7' : m.score >= 0.5 ? '#fef3c7' : '#fee2e2',
                          color: m.score >= 0.8 ? '#15803d' : m.score >= 0.5 ? '#a16207' : '#b91c1c',
                        }}>
                          {Math.round((m.score || 0) * 100)}%
                        </span>
                      </td>
                      <td style={{ ...tdStyle, color: '#6b7280', fontSize: 13 }}>{fmtDate(m.matchedAt)}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Drei-Schritte-Erklärung */}
        <div style={{ marginTop: 36 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18,
          }}>
            <div style={{ height: 1, flex: 1, background: '#e5e7eb' }} />
            <div style={{
              fontSize: 11, fontWeight: 700, color: '#9ca3af',
              letterSpacing: '0.18em', textTransform: 'uppercase',
            }}>
              So funktioniert es
            </div>
            <div style={{ height: 1, flex: 1, background: '#e5e7eb' }} />
          </div>

          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20,
          }}>
            {[
              { num: '01', title: 'Suchprofil anlegen', desc: 'Definieren Sie Studiengang, Standort und weitere Wunschkriterien für Ihre Talentsuche.' },
              { num: '02', title: 'Talente finden', desc: 'Wir gleichen Ihr Profil mit interessierten Studieninteressierten ab — automatisch und kontinuierlich.' },
              { num: '03', title: 'Direkt kontaktieren', desc: 'Sie werden automatisch informiert und können Gespräche mit passenden Kandidaten starten.' },
            ].map((step) => (
              <div
                key={step.num}
                style={{
                  background: 'white', borderRadius: 14, padding: '24px 26px',
                  border: '1px solid #e5e7eb', position: 'relative', overflow: 'hidden',
                }}
              >
                <div style={{
                  position: 'absolute', top: -18, right: -8,
                  fontSize: 90, fontWeight: 900, color: '#f0fdfb',
                  letterSpacing: '-0.04em', lineHeight: 1, userSelect: 'none',
                  fontFamily: "'Neue-Haas-Grotesk-Display-Pro', Helvetica, Arial, sans-serif",
                }}>
                  {step.num}
                </div>
                <div style={{ position: 'relative' }}>
                  <div style={{
                    fontSize: 11, fontWeight: 700, color: '#009B8D',
                    letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 8,
                  }}>
                    Schritt {step.num}
                  </div>
                  <div style={{
                    fontSize: 17, fontWeight: 800, color: '#0B2623',
                    marginBottom: 8, letterSpacing: '-0.01em',
                  }}>
                    {step.title}
                  </div>
                  <div style={{ fontSize: 13, color: '#6b7280', lineHeight: 1.6 }}>
                    {step.desc}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ── Erweiterte StatCard fuer das Dashboard ───────────────── */

function DashStatCard({ label, value, color, icon, hint }: {
  label: string
  value: number
  color: string
  icon: string
  hint: string
}) {
  return (
    <div style={{
      background: 'white', borderRadius: 16, padding: '26px 28px',
      border: '1px solid #e5e7eb', position: 'relative', overflow: 'hidden',
      boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
      transition: 'all .2s',
    }}>
      {/* Akzent-Linie oben */}
      <div style={{
        position: 'absolute', top: 0, left: 0, right: 0, height: 3,
        background: `linear-gradient(90deg, ${color}, ${color}80)`,
      }} />
      {/* Großes Icon im Hintergrund */}
      <div style={{
        position: 'absolute', top: 16, right: 20, fontSize: 32,
        opacity: 0.15, filter: 'grayscale(0.2)',
      }}>
        {icon}
      </div>
      <div style={{
        fontSize: 11, fontWeight: 700, color: '#9ca3af',
        letterSpacing: '0.15em', textTransform: 'uppercase', marginBottom: 10,
      }}>
        {label}
      </div>
      <div style={{
        fontSize: 44, fontWeight: 800, color: '#0B2623',
        letterSpacing: '-0.03em', lineHeight: 1, fontVariantNumeric: 'tabular-nums',
        fontFamily: "'Neue-Haas-Grotesk-Display-Pro', Helvetica, Arial, sans-serif",
      }}>
        {value.toLocaleString('de-DE')}
      </div>
      <div style={{
        fontSize: 12, color: '#6b7280', marginTop: 8,
        display: 'flex', alignItems: 'center', gap: 6,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0,
        }} />
        {hint}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Suchprofile
// ═══════════════════════════════════════════════════════════════

function SearchProfilesView() {
  const [profiles, setProfiles] = useState<SearchProfile[]>([])
  const [loading, setLoading] = useState(true)
  const [showForm, setShowForm] = useState(false)
  const [editing, setEditing] = useState<SearchProfile | null>(null)
  const [expanded, setExpanded] = useState<string | null>(null)

  const load = () => { request<any>('/search-profiles').then(r => { setProfiles(r.data || []); setLoading(false) }).catch(() => setLoading(false)) }
  useEffect(() => { load() }, [])

  const toggle = async (id: string) => { await request<any>(`/search-profiles/${id}/toggle`, { method: 'PATCH' }); load() }
  const del = async (id: string) => { if (!confirm('Suchprofil wirklich löschen?')) return; await request<any>(`/search-profiles/${id}`, { method: 'DELETE' }); load() }

  if (loading) return <Loading />

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '36px 56px 56px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <h1 style={h1Style}>Suchprofile</h1>
        <button onClick={() => { setEditing(null); setShowForm(true) }} style={btnPrimary}>+ Neues Suchprofil</button>
      </div>
      {showForm && <ProfileForm profile={editing} onSave={() => { setShowForm(false); setEditing(null); load() }} onCancel={() => { setShowForm(false); setEditing(null) }} />}
      {profiles.length === 0 && !showForm ? (
        <Card><div style={{ padding: '60px 40px', textAlign: 'center' }}><h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Noch keine Suchprofile</h3><p style={{ color: '#999', maxWidth: 400, margin: '0 auto', fontSize: 15 }}>Erstellen Sie Ihr erstes Suchprofil, um passende Bewerber zu finden.</p></div></Card>
      ) : (
        <div style={{ display: 'grid', gap: 16 }}>
          {profiles.map(p => (
            <Card key={p.id} borderLeft={p.isActive ? '#009B8D' : '#e5e7eb'}>
              <div style={{ padding: '24px 28px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                      <h3 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>{p.name}</h3>
                      <Tag color={p.isActive ? 'teal' : 'gray'}>{p.isActive ? 'Aktiv' : 'Inaktiv'}</Tag>
                    </div>
                    {p.description && <p style={{ margin: '0 0 12px', color: '#999', fontSize: 15 }}>{p.description}</p>}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {p.criteria.map((c, i) => (
                        <span key={i} style={{ background: '#f9fafb', padding: '6px 14px', borderRadius: 8, fontSize: 13, border: '1px solid #e5e7eb' }}>
                          {FILTER_FIELDS.find(f => f.value === c.field)?.label || c.field} {OPERATORS.find(o => o.value === c.operator)?.label || c.operator} <strong>{c.value}</strong>
                        </span>
                      ))}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    <PillBtn onClick={() => setExpanded(expanded === p.id ? null : p.id)} teal>{expanded === p.id ? 'Treffer ausblenden' : 'Treffer anzeigen'}</PillBtn>
                    <PillBtn onClick={() => toggle(p.id)}>{p.isActive ? 'Deaktivieren' : 'Aktivieren'}</PillBtn>
                    <PillBtn onClick={() => { setEditing(p); setShowForm(true) }}>Bearbeiten</PillBtn>
                    {!p.isActive && <PillBtn onClick={() => del(p.id)} danger>Löschen</PillBtn>}
                  </div>
                </div>
              </div>
              {expanded === p.id && <MatchingLeadsPanel profileId={p.id} />}
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}

function MatchingLeadsPanel({ profileId }: { profileId: string }) {
  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState(true)
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const { favs: favorites, toggle: toggleFav } = useDualMatchFavorites()
  const [detailLead, setDetailLead] = useState<Lead | null>(null)
  const [contactData, setContactData] = useState<Record<string, { email: string; telefon: string; hochschulzentrum: string; produkt: string; studienstart: string; hasCv: boolean }>>({})
  const [contactLoading, setContactLoading] = useState(false)

  useEffect(() => {
    setLoading(true)
    request<any>(`/search-profiles/${profileId}/leads?page=${page}&pageSize=9`)
      .then(r => { setLeads(r.data || []); setTotal(r.total || 0); setLoading(false) })
      .catch(() => setLoading(false))
  }, [profileId, page])

  const totalPages = Math.ceil(total / 9)

  const openDetail = async (lead: Lead) => {
    setDetailLead(lead)
    if (!contactData[lead.id]) {
      setContactLoading(true)
      try {
        const r = await request<any>(`/leads/${lead.id}/contact`)
        setContactData(prev => ({ ...prev, [lead.id]: { email: r.data.email, telefon: r.data.telefon, hochschulzentrum: r.data.hochschulzentrum || '', produkt: r.data.produkt || '', studienstart: r.data.studienstart || '', hasCv: !!r.data.hasCv } }))
      } catch {
        setContactData(prev => ({ ...prev, [lead.id]: { email: '', telefon: '', hochschulzentrum: '', produkt: '', studienstart: '', hasCv: false } }))
      } finally {
        setContactLoading(false)
      }
    }
  }

  return (
    <div style={{ borderTop: '1px solid #e5e7eb', background: '#f9fafb' }}>
      <div style={{ padding: '16px 28px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 14, fontWeight: 700 }}>
          {loading ? 'Suche...' : `${total} passende Bewerber`}
        </span>
      </div>
      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>Laden...</div>
      ) : leads.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: '#999' }}>Keine passenden Bewerber.</div>
      ) : (
        <>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
            gap: 24, padding: '0 28px 28px',
          }}>
            {leads.map(l => (
              <LeadDetailCard
                key={l.id}
                lead={l}
                isFav={favorites.has(l.id)}
                onToggleFav={() => toggleFav(l.id)}
                onOpenDetail={() => openDetail(l)}
              />
            ))}
          </div>
          {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onChange={setPage} />}
        </>
      )}

      {detailLead && (
        <DetailOverlay
          lead={detailLead}
          contact={contactData[detailLead.id]}
          contactLoading={contactLoading}
          isFav={favorites.has(detailLead.id)}
          onToggleFav={() => toggleFav(detailLead.id)}
          onClose={() => setDetailLead(null)}
        />
      )}
    </div>
  )
}

/* ── Wiederverwendbare LeadDetailCard (gleicher Look in LeadsView und MatchingLeadsPanel) ── */

function LeadDetailCard({
  lead, isFav, onToggleFav, onOpenDetail,
}: {
  lead: Lead
  isFav: boolean
  onToggleFav: () => void
  onOpenDetail: () => void
}) {
  return (
    <div style={{
      background: 'white', borderRadius: 12,
      border: `1.5px solid ${isFav ? '#f59e0b' : '#e5e7eb'}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      transition: 'all .2s', boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ padding: '24px 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <h3
            onClick={onOpenDetail}
            style={{
              margin: '0 0 6px', fontSize: 18, fontWeight: 800, color: '#0B2623',
              letterSpacing: '-0.01em', cursor: 'pointer',
              transition: 'color .15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.color = '#009B8D')}
            onMouseLeave={(e) => (e.currentTarget.style.color = '#0B2623')}
            title="Details anzeigen"
          >
            {lead.firstName} {lead.lastName}
          </h3>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleFav() }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 20, color: isFav ? '#f59e0b' : '#d1d5db',
              padding: 0, lineHeight: 1,
            }}
            title={isFav ? 'Aus Favoriten entfernen' : 'Als Favorit markieren'}
          >
            {isFav ? '★' : '☆'}
          </button>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {lead.hochschulzentrum && <Tag>{lead.hochschulzentrum}</Tag>}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 5, fontSize: 14 }}>
          <FieldRow label="Studiengang" value={lead.studiengang} />
          <FieldRow label="Ausbildungsberuf" value={lead.beruf} />
          <FieldRow label="Eingangsdatum" value={fmtDate(lead.importedAt)} />
          <FieldRow label="Arbeitsort" value={lead.standort} />
        </div>
      </div>
      <div style={{ marginTop: 'auto', padding: '16px 24px 20px' }}>
        <button
          onClick={onOpenDetail}
          style={{
            width: '100%', textAlign: 'center', padding: 10,
            color: '#009B8D', border: '1.5px solid #009B8D',
            borderRadius: 20, fontSize: 13, fontWeight: 700,
            cursor: 'pointer', background: 'white', transition: 'all .15s',
          }}
        >
          Details anzeigen
        </button>
      </div>
    </div>
  )
}

function ProfileForm({ profile, onSave, onCancel }: { profile: SearchProfile | null; onSave: () => void; onCancel: () => void }) {
  const [name, setName] = useState(profile?.name || ''); const [description, setDescription] = useState(profile?.description || '')
  const [criteria, setCriteria] = useState<SearchCriteria[]>(profile?.criteria || [{ field: 'studiengang', operator: 'EQUALS', value: '' }])
  const [error, setError] = useState(''); const [saving, setSaving] = useState(false)
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault(); setError(''); setSaving(true)
    const valid = criteria.filter(c => c.value.trim())
    if (!valid.length) { setError('Mindestens ein Kriterium mit Wert erforderlich'); setSaving(false); return }
    try {
      const data = { name, description, criteria: valid }
      if (profile) await request<any>(`/search-profiles/${profile.id}`, { method: 'PUT', body: JSON.stringify(data) })
      else await request<any>('/search-profiles', { method: 'POST', body: JSON.stringify(data) })
      onSave()
    } catch (err: any) { setError(err.message) } finally { setSaving(false) }
  }
  return (
    <div style={{ background: 'white', borderRadius: 12, padding: 28, marginBottom: 24, border: '2px solid #009B8D', boxShadow: '0 24px 80px rgba(0,0,0,0.08)' }}>
      <h3 style={{ margin: '0 0 20px', fontSize: 20, fontWeight: 700 }}>{profile ? 'Suchprofil bearbeiten' : 'Neues Suchprofil erstellen'}</h3>
      {error && <div style={{ background: '#FEE2E2', color: '#dc2626', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 14 }}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 16, marginBottom: 20 }}>
          <div><label style={labelStyle}>Name</label><input value={name} onChange={e => setName(e.target.value)} required style={inputStyle} placeholder="z.B. Marketing Düsseldorf" /></div>
          <div><label style={labelStyle}>Beschreibung</label><input value={description} onChange={e => setDescription(e.target.value)} style={inputStyle} placeholder="Optional..." /></div>
        </div>
        <label style={{ ...labelStyle, marginBottom: 12 }}>Suchkriterien</label>
        {criteria.map((c, i) => (
          <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr auto', gap: 10, marginBottom: 10, alignItems: 'end' }}>
            <select value={c.field} onChange={e => setCriteria(criteria.map((x, j) => j === i ? { ...x, field: e.target.value } : x))} style={inputStyle}>{FILTER_FIELDS.map(f => <option key={f.value} value={f.value}>{f.label}</option>)}</select>
            <select value={c.operator} onChange={e => setCriteria(criteria.map((x, j) => j === i ? { ...x, operator: e.target.value } : x))} style={inputStyle}>{OPERATORS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}</select>
            <input value={c.value} onChange={e => setCriteria(criteria.map((x, j) => j === i ? { ...x, value: e.target.value } : x))} style={inputStyle} placeholder={c.operator === 'IN' ? 'Kommasepariert' : 'Wert'} />
            {criteria.length > 1 && <PillBtn onClick={() => setCriteria(criteria.filter((_, j) => j !== i))} danger>×</PillBtn>}
          </div>
        ))}
        <PillBtn onClick={() => setCriteria([...criteria, { field: 'studiengang', operator: 'EQUALS', value: '' }])} teal>+ Kriterium</PillBtn>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', marginTop: 16 }}>
          <PillBtn onClick={onCancel}>Abbrechen</PillBtn>
          <button type="submit" disabled={saving} style={btnPrimary}>{saving ? '...' : (profile ? 'Aktualisieren' : 'Erstellen')}</button>
        </div>
      </form>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Alle Bewerber
// ═══════════════════════════════════════════════════════════════

function LeadsView() {
  const [allLeads, setAllLeads] = useState<Lead[]>([]); const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState(''); const [filterStudiengang, setFilterStudiengang] = useState('')
  const [filterStandort, setFilterStandort] = useState(''); const [filterAbschluss, setFilterAbschluss] = useState('')
  const [filterSemester, setFilterSemester] = useState(''); const [filterHasCV, setFilterHasCV] = useState(false)
  const [showFavsOnly, setShowFavsOnly] = useState(false); const { favs: favorites, toggle: toggleFav } = useDualMatchFavorites()
  const [detailLead, setDetailLead] = useState<Lead | null>(null)
  const [contactData, setContactData] = useState<Record<string, { email: string; telefon: string; hochschulzentrum: string; produkt: string; studienstart: string; hasCv: boolean }>>({})
  const [contactLoading, setContactLoading] = useState(false)
  const [page, setPage] = useState(1); const pageSize = 9
  const [filterDoubletten, setFilterDoubletten] = useState(false)

  // Doubletten: contact_id → Anzahl Leads mit gleicher contact_id
  const contactCounts = useMemo(() => {
    const counts: Record<string, number> = {}
    for (const l of allLeads) {
      if (l.contactId) counts[l.contactId] = (counts[l.contactId] || 0) + 1
    }
    return counts
  }, [allLeads])

  // Siblings für DetailOverlay (alle Leads gleicher Person, exkl. aktueller)
  const detailSiblings = useMemo(() => {
    if (!detailLead?.contactId) return []
    return allLeads.filter(l => l.contactId === detailLead.contactId && l.id !== detailLead.id)
  }, [allLeads, detailLead])

  useEffect(() => {
    async function init() {
      try {
        const r = await request<any>('/dashboard/leads?page=1&pageSize=9999')
        const data = r.data || []
        if (data.length === 0) {
          await request<any>('/sync/trigger', { method: 'POST' })
          const r2 = await request<any>('/dashboard/leads?page=1&pageSize=9999')
          setAllLeads(r2.data || [])
        } else { setAllLeads(data) }
      } catch {} finally { setLoading(false) }
    }
    init()
  }, [])

  const uniqueStudiengaenge = useMemo(() => [...new Set(allLeads.map(l => l.studiengang).filter(Boolean))].sort(), [allLeads])
  const uniqueStandorte = useMemo(() => [...new Set(allLeads.map(l => l.hochschulzentrum).filter(Boolean))].sort(), [allLeads])
  const uniqueAbschluesse = useMemo(() => [...new Set(allLeads.map(l => l.abschlussart).filter(Boolean))].sort(), [allLeads])
  const uniqueSemester = useMemo(() => [...new Set(allLeads.map(l => l.startzeitpunkt).filter(Boolean))].sort(), [allLeads])

  const filtered = useMemo(() => allLeads.filter(l => {
    if (showFavsOnly && !favorites.has(l.id)) return false
    if (filterHasCV && !l.hasFiles) return false
    if (filterDoubletten && !(l.contactId && contactCounts[l.contactId] > 1)) return false
    if (filterStudiengang && l.studiengang !== filterStudiengang) return false
    if (filterStandort && l.hochschulzentrum !== filterStandort) return false
    if (filterAbschluss && l.abschlussart !== filterAbschluss) return false
    if (filterSemester && l.startzeitpunkt !== filterSemester) return false
    if (search) { const s = search.toLowerCase(); if (![l.firstName, l.lastName, l.studiengang, l.standort, l.beruf, l.hochschulzentrum].join(' ').toLowerCase().includes(s)) return false }
    return true
  }), [allLeads, showFavsOnly, favorites, filterHasCV, filterDoubletten, contactCounts, filterStudiengang, filterStandort, filterAbschluss, filterSemester, search])

  const sorted = useMemo(() => [...filtered].sort((a, b) => {
    const da = a.importedAt ? new Date(a.importedAt).getTime() : 0
    const db = b.importedAt ? new Date(b.importedAt).getTime() : 0
    return db - da
  }), [filtered])
  const totalPages = Math.ceil(sorted.length / pageSize)
  const pageLeads = sorted.slice((page - 1) * pageSize, page * pageSize)

  const openDetail = async (lead: Lead) => {
    setDetailLead(lead)
    if (!contactData[lead.id]) {
      setContactLoading(true)
      try { const r = await request<any>(`/leads/${lead.id}/contact`); setContactData(prev => ({ ...prev, [lead.id]: { email: r.data.email, telefon: r.data.telefon, hochschulzentrum: r.data.hochschulzentrum || '', produkt: r.data.produkt || '', studienstart: r.data.studienstart || '', hasCv: !!r.data.hasCv } })) }
      catch { setContactData(prev => ({ ...prev, [lead.id]: { email: '', telefon: '', hochschulzentrum: '', produkt: '', studienstart: '', hasCv: false } })) }
      finally { setContactLoading(false) }
    }
  }
  const resetFilters = () => { setFilterStudiengang(''); setFilterStandort(''); setFilterAbschluss(''); setFilterSemester(''); setFilterHasCV(false); setFilterDoubletten(false); setSearch(''); setShowFavsOnly(false); setPage(1) }
  const handleSearch = (e: React.FormEvent) => { e.preventDefault(); setPage(1) }

  if (loading) return <Loading />

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '36px 56px 56px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ ...h1Style, marginBottom: 0 }}>Bewerber</h1>
        <PillBtn onClick={() => { setShowFavsOnly(!showFavsOnly); setPage(1) }} teal={showFavsOnly}>★ Favoriten {showFavsOnly ? 'ausblenden' : `(${favorites.size})`}</PillBtn>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: 16, background: 'white', borderRadius: 16, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', flex: 1 }}>
          <input value={search} onChange={e => { setSearch(e.target.value); setPage(1) }} placeholder="Suche nach Name, Studiengang, Standort..." style={{ flex: 1, padding: '14px 28px', border: 'none', fontSize: 16, outline: 'none', background: 'transparent' }} />
        </form>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 12 }}>
        <select value={filterStudiengang} onChange={e => { setFilterStudiengang(e.target.value); setPage(1) }} style={pillSelectStyle}><option value="">Studiengang</option>{uniqueStudiengaenge.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filterStandort} onChange={e => { setFilterStandort(e.target.value); setPage(1) }} style={pillSelectStyle}><option value="">Hochschulzentrum</option>{uniqueStandorte.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filterSemester} onChange={e => { setFilterSemester(e.target.value); setPage(1) }} style={pillSelectStyle}><option value="">Wunschstartsemester</option>{uniqueSemester.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <select value={filterAbschluss} onChange={e => { setFilterAbschluss(e.target.value); setPage(1) }} style={pillSelectStyle}><option value="">Bildungsabschluss</option>{uniqueAbschluesse.map(s => <option key={s} value={s}>{s}</option>)}</select>
        <div style={{ ...pillSelectStyle, color: '#6b7280', fontWeight: 600, textAlign: 'center' }}>
          {filtered.length}{filtered.length !== allLeads.length ? ` / ${allLeads.length}` : ''} Bewerber
        </div>
      </div>

      {/* Zusätzliche Optionen */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <label
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 16px', borderRadius: 999,
            background: filterHasCV ? '#e6f7f5' : 'white',
            border: `1.5px solid ${filterHasCV ? '#009B8D' : '#e5e7eb'}`,
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            color: filterHasCV ? '#006B5F' : '#6b7280',
            transition: 'all .15s', userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={filterHasCV}
            onChange={e => { setFilterHasCV(e.target.checked); setPage(1) }}
            style={{ accentColor: '#009B8D', width: 16, height: 16, cursor: 'pointer' }}
          />
          <FileText size={14} />
          Nur mit Lebenslauf
        </label>
        <label
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '8px 16px', borderRadius: 999,
            background: filterDoubletten ? '#fef3c7' : 'white',
            border: `1.5px solid ${filterDoubletten ? '#f59e0b' : '#e5e7eb'}`,
            fontSize: 13, fontWeight: 600, cursor: 'pointer',
            color: filterDoubletten ? '#92400e' : '#6b7280',
            transition: 'all .15s', userSelect: 'none',
          }}
        >
          <input
            type="checkbox"
            checked={filterDoubletten}
            onChange={e => { setFilterDoubletten(e.target.checked); setPage(1) }}
            style={{ accentColor: '#f59e0b', width: 16, height: 16, cursor: 'pointer' }}
          />
          <Users size={14} />
          Nur Doubletten ({Object.values(contactCounts).filter(c => c > 1).length} Personen)
        </label>
        {(filterStudiengang || filterStandort || filterAbschluss || filterSemester || filterHasCV || filterDoubletten || search || showFavsOnly) && (
          <PillBtn onClick={resetFilters} danger>× Filter zurücksetzen</PillBtn>
        )}
      </div>

      {filtered.length === 0 ? (
        <Card><div style={{ padding: '60px 40px', textAlign: 'center' }}><h3 style={{ fontSize: 20, fontWeight: 700, marginBottom: 8 }}>Keine Bewerber gefunden</h3></div></Card>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 28 }}>
            {pageLeads.map(l => (
              <div key={l.id} style={{ background: 'white', borderRadius: 8, border: `1.5px solid ${favorites.has(l.id) ? '#f59e0b' : '#e5e7eb'}`, display: 'flex', flexDirection: 'column', overflow: 'hidden', transition: 'all .25s' }}>
                <div style={{ padding: '28px 28px 0' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                    <LeadAvatar lead={l} size={52} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <h3
                          onClick={() => openDetail(l)}
                          style={{
                            margin: '0 0 6px', fontSize: 20, fontWeight: 700,
                            cursor: 'pointer', transition: 'color .15s',
                          }}
                          onMouseEnter={(e) => (e.currentTarget.style.color = '#009B8D')}
                          onMouseLeave={(e) => (e.currentTarget.style.color = '')}
                          title="Details anzeigen"
                        >
                          {l.firstName} {l.lastName}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                          {l.contactId && contactCounts[l.contactId] > 1 && (
                            <span style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '2px 8px', borderRadius: 999,
                              background: '#fef3c7', color: '#92400e',
                              fontSize: 11, fontWeight: 700, border: '1px solid #fde68a',
                              whiteSpace: 'nowrap',
                            }}
                              title={`${contactCounts[l.contactId]} Bewerbungen von dieser Person`}
                            >
                              {contactCounts[l.contactId]}× Bewerbungen
                            </span>
                          )}
                          {l.hochschulzentrum && <Tag>{l.hochschulzentrum}</Tag>}
                          <button onClick={() => toggleFav(l.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 22, color: favorites.has(l.id) ? '#f59e0b' : '#d1d5db', padding: 0, lineHeight: 1 }}>{favorites.has(l.id) ? '★' : '☆'}</button>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 14 }}>
                    <FieldRow label="Studiengang" value={l.studiengang} />
                    <FieldRow label="Ausbildungsberuf" value={l.beruf} />
                    <FieldRow label="Eingangsdatum" value={fmtDate(l.importedAt)} />
                    <FieldRow label="Arbeitsort" value={l.standort} />
                  </div>
                </div>
                <div style={{ marginTop: 'auto', padding: '16px 28px 20px' }}>
                  <button onClick={() => openDetail(l)} style={{ width: '100%', textAlign: 'center', padding: 10, color: '#009B8D', border: '1.5px solid #009B8D', borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: 'pointer', background: 'white', transition: 'all .15s' }}>Details anzeigen</button>
                </div>
              </div>
            ))}
          </div>
          {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onChange={setPage} />}
        </>
      )}

      {detailLead && <DetailOverlay lead={detailLead} contact={contactData[detailLead.id]} contactLoading={contactLoading} isFav={favorites.has(detailLead.id)} onToggleFav={() => toggleFav(detailLead.id)} onClose={() => setDetailLead(null)} siblings={detailSiblings} onSelectSibling={openDetail} />}
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Detail Overlay – elegantes Kandidaten-Profil
// ═══════════════════════════════════════════════════════════════

function DetailOverlay({ lead, contact, contactLoading, isFav, onToggleFav, onClose, siblings = [], onSelectSibling }: {
  lead: Lead; contact?: { email: string; telefon: string; hochschulzentrum: string; produkt: string; studienstart: string; hasCv: boolean }; contactLoading: boolean; isFav: boolean; onToggleFav: () => void; onClose: () => void; siblings?: Lead[]; onSelectSibling?: (lead: Lead) => void
}) {
  // ESC-Taste schließt
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    window.addEventListener('keydown', onKey)
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('keydown', onKey)
      document.body.style.overflow = ''
    }
  }, [onClose])

  const initials = `${lead.firstName?.charAt(0) || ''}${lead.lastName?.charAt(0) || ''}`.toUpperCase() || '?'
  // Hochschulzentrum, Produkt & Studienstart kommen live aus dem Contact
  // (die Lead-Liste liefert sie noch nicht – Quelle ist der Bewerber-Contact)
  const hochschulzentrum = contact?.hochschulzentrum || lead.hochschulzentrum || ''
  const produkt = contact?.produkt || lead.studiengang || ''
  const studienstart = contact?.studienstart || lead.startzeitpunkt || ''
  const [photoFailed, setPhotoFailed] = useState(false)
  const showPhoto = !!lead.hasFiles && !photoFailed
  const photoUrl = `/api/dualmatch/leads/${lead.id}/photo`

  const interessenList = Array.isArray(lead.interessen)
    ? lead.interessen
    : (lead.interessen ? [String(lead.interessen)] : [])

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(11, 38, 35, 0.55)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
        zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 24,
        animation: 'dm-fade-in .18s ease-out',
      }}
    >
      <style>{`
        @keyframes dm-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes dm-pop-in { from { opacity: 0; transform: translateY(12px) scale(.98) } to { opacity: 1; transform: translateY(0) scale(1) } }
      `}</style>

      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'white', borderRadius: 24, width: 'min(680px, 100%)',
          maxHeight: '90vh', overflow: 'hidden',
          boxShadow: '0 32px 96px rgba(11,38,35,0.32), 0 8px 24px rgba(0,0,0,0.08)',
          display: 'flex', flexDirection: 'column',
          animation: 'dm-pop-in .22s cubic-bezier(.2,.9,.3,1.2)',
        }}
      >
        {/* ── Header mit Verlauf ── */}
        <div style={{
          position: 'relative',
          padding: '32px 36px 28px',
          background: 'linear-gradient(135deg, #0B2623 0%, #006B5F 60%, #009B8D 100%)',
          color: 'white',
          flexShrink: 0,
        }}>
          {/* dezente Akzent-Form */}
          <div style={{
            position: 'absolute', top: -40, right: -40, width: 200, height: 200,
            borderRadius: '50%', background: 'rgba(255,255,255,0.06)',
          }} />

          <button
            onClick={onClose}
            aria-label="Schließen"
            style={{
              position: 'absolute', top: 18, right: 18,
              width: 36, height: 36, borderRadius: '50%',
              background: 'rgba(255,255,255,0.12)', border: 'none',
              color: 'white', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              transition: 'background .15s',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.22)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'rgba(255,255,255,0.12)')}
          >
            <X size={18} />
          </button>

          <div style={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 20 }}>
            {/* Avatar – Foto aus Lebenslauf, Fallback Initialen */}
            <div style={{
              width: 84, height: 84, borderRadius: '50%',
              background: 'linear-gradient(135deg, #ffffff 0%, #ccf3ef 100%)',
              color: '#006B5F', fontSize: 26, fontWeight: 800,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0, overflow: 'hidden',
              boxShadow: '0 10px 28px rgba(0,0,0,0.22)',
              border: '3px solid rgba(255,255,255,0.25)',
              fontFamily: "'Neue-Haas-Grotesk-Display-Pro', Helvetica, Arial, sans-serif",
            }}>
              {showPhoto ? (
                <img
                  src={photoUrl}
                  alt={`${lead.firstName} ${lead.lastName}`}
                  onError={() => setPhotoFailed(true)}
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover', objectPosition: 'center top',
                    display: 'block',
                  }}
                />
              ) : initials}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 11, fontWeight: 700, opacity: 0.75,
                letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 4,
              }}>
                Kandidaten-Profil
              </div>
              <h2 style={{
                margin: 0, fontSize: 26, fontWeight: 900, letterSpacing: '-0.02em',
                fontFamily: "'Neue-Haas-Grotesk-Display-Pro', Helvetica, Arial, sans-serif",
                lineHeight: 1.15,
              }}>
                {lead.firstName} {lead.lastName}
              </h2>
              <div style={{
                marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 14,
                fontSize: 13, opacity: 0.92,
              }}>
                {lead.studiengang && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <GraduationCap size={14} /> {lead.studiengang}
                  </span>
                )}
                {hochschulzentrum && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                    <MapPin size={14} /> {hochschulzentrum}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* ── Scrollbarer Body ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '0' }}>

          {/* Kontakt-Aktionen */}
          <div style={{
            padding: '20px 36px',
            background: '#fafbfa',
            borderBottom: '1px solid #f0f0f0',
          }}>
            <div style={{
              fontSize: 10, fontWeight: 700, color: '#9ca3af',
              letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: 12,
            }}>
              Kontakt aufnehmen
            </div>
            {contactLoading ? (
              <div style={{
                padding: '14px 0', fontSize: 13, color: '#9ca3af',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <div style={{
                  width: 14, height: 14, border: '2px solid #e5e7eb',
                  borderTopColor: '#009B8D', borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite',
                }} />
                <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
                Kontaktdaten werden geladen ...
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                <ContactAction
                  icon={<Phone size={18} />}
                  label="Anrufen"
                  detail={contact?.telefon || 'Keine Nummer hinterlegt'}
                  href={contact?.telefon ? `tel:${contact.telefon}` : undefined}
                  primary
                />
                <ContactAction
                  icon={<Mail size={18} />}
                  label="E-Mail"
                  detail={contact?.email || 'Keine E-Mail hinterlegt'}
                  href={contact?.email ? `mailto:${contact.email}` : undefined}
                />
                <button
                  onClick={onToggleFav}
                  title={isFav ? 'Aus Favoriten entfernen' : 'Als Favorit markieren'}
                  style={{
                    width: 52, height: 52, borderRadius: 14,
                    background: isFav ? '#fffbeb' : 'white',
                    border: `1.5px solid ${isFav ? '#fbbf24' : '#e5e7eb'}`,
                    color: isFav ? '#d97706' : '#9ca3af',
                    cursor: 'pointer', flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'all .15s',
                  }}
                >
                  <Star size={20} fill={isFav ? '#fbbf24' : 'none'} />
                </button>
              </div>
            )}
          </div>

          {/* Studienprofil */}
          <Section title="Studienprofil" icon={<GraduationCap size={14} />}>
            <ProfileGrid>
              <ProfileItem label="Studiengang" value={produkt} icon={<GraduationCap size={14} />} />
              <ProfileItem label="Wunschstartsemester" value={studienstart} icon={<Calendar size={14} />} />
              <ProfileItem label="Hochschulzentrum" value={hochschulzentrum} icon={<Building2 size={14} />} />
              <ProfileItem label="Bildungsabschluss" value={lead.abschlussart} icon={<Award size={14} />} />
            </ProfileGrid>
          </Section>

          {/* Berufsprofil */}
          <Section title="Beruf & Praxis" icon={<Briefcase size={14} />}>
            <ProfileGrid>
              <ProfileItem label="Ausbildungsberuf" value={lead.beruf} icon={<Briefcase size={14} />} />
              <ProfileItem label="Branche" value={lead.fachrichtung} icon={<Sparkles size={14} />} />
              <ProfileItem label="Wunscharbeitsort" value={lead.standort} icon={<MapPin size={14} />} />
              <ProfileItem label="Beschäftigungsform" value={lead.beschaeftigungsmodell} icon={<Briefcase size={14} />} />
            </ProfileGrid>
            {lead.erfahrungen && (
              <div style={{ marginTop: 14 }}>
                <DetailNote label="Erfahrungen">{lead.erfahrungen}</DetailNote>
              </div>
            )}
            {lead.sprachen && (
              <div style={{ marginTop: 10, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                <Languages size={14} style={{ color: '#9ca3af', marginTop: 2, flexShrink: 0 }} />
                <div style={{ fontSize: 13, color: '#374151' }}>
                  <span style={{ color: '#9ca3af' }}>Sprachen: </span>
                  {lead.sprachen}
                </div>
              </div>
            )}
          </Section>

          {/* Interessen */}
          {interessenList.length > 0 && (
            <Section title="Interessen" icon={<Sparkles size={14} />}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {interessenList.map((it, i) => (
                  <span key={i} style={{
                    padding: '5px 12px', borderRadius: 999,
                    background: '#f0fdfb', color: '#006B5F',
                    fontSize: 12, fontWeight: 600,
                    border: '1px solid #ccf3ef',
                  }}>
                    {it}
                  </span>
                ))}
              </div>
            </Section>
          )}

          {/* Eingang */}
          <Section title="Eingang" icon={<Calendar size={14} />}>
            <div style={{ fontSize: 13, color: '#374151' }}>
              <Calendar size={14} style={{ display: 'inline', verticalAlign: 'middle', color: '#9ca3af', marginRight: 6 }} />
              Eingegangen am <strong>{fmtDate(lead.importedAt)}</strong>
            </div>
          </Section>

          {/* Weitere Bewerbungen (Doubletten) */}
          {siblings.length > 0 && (
            <Section title={`Weitere Bewerbungen dieser Person (${siblings.length})`} icon={<Users size={14} />}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {siblings.map(s => (
                  <div
                    key={s.id}
                    onClick={() => onSelectSibling?.(s)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 14,
                      padding: '12px 14px', borderRadius: 12,
                      background: '#fafbfa', border: '1px solid #f0f0f0',
                      cursor: 'pointer', transition: 'all .15s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#f0fdfb'; e.currentTarget.style.borderColor = '#009B8D' }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#fafbfa'; e.currentTarget.style.borderColor = '#f0f0f0' }}
                  >
                    <LeadAvatar lead={s} size={40} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 14, fontWeight: 700, color: '#0B2623' }}>
                        {s.firstName} {s.lastName}
                      </div>
                      <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2, display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {s.studiengang && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <GraduationCap size={11} /> {s.studiengang}
                          </span>
                        )}
                        {s.beruf && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Briefcase size={11} /> {s.beruf}
                          </span>
                        )}
                        {s.startzeitpunkt && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <Calendar size={11} /> {s.startzeitpunkt}
                          </span>
                        )}
                        {s.hochschulzentrum && (
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <MapPin size={11} /> {s.hochschulzentrum}
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      {s.hasFiles && (
                        <span style={{
                          display: 'inline-flex', alignItems: 'center', gap: 4,
                          padding: '2px 8px', borderRadius: 999,
                          background: '#f0fdfb', color: '#006B5F',
                          fontSize: 10, fontWeight: 700, border: '1px solid #ccf3ef',
                        }}>
                          <FileText size={10} /> CV
                        </span>
                      )}
                      <span style={{ fontSize: 11, color: '#9ca3af' }}>{fmtDate(s.importedAt)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Section>
          )}
        </div>

        {/* ── Footer mit Lebenslauf-CTA (nur wenn CV vorhanden) ── */}
        <div style={{
          flexShrink: 0,
          padding: '18px 36px',
          borderTop: '1px solid #f0f0f0',
          background: 'white',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
        }}>
          <div style={{ fontSize: 12, color: '#9ca3af' }}>
            ID: <code style={{ fontSize: 11, color: '#6b7280' }}>{lead.id.slice(0, 8)}</code>
          </div>
          {(contact?.hasCv || lead.hasFiles) ? (
            <a
              href={`/api/dualmatch/leads/${lead.id}/cv`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                padding: '11px 22px',
                background: '#0B2623', color: 'white',
                borderRadius: 999, fontSize: 13, fontWeight: 700,
                textDecoration: 'none', transition: 'all .15s',
                boxShadow: '0 4px 12px rgba(11,38,35,0.18)',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = '#009B8D')}
              onMouseLeave={(e) => (e.currentTarget.style.background = '#0B2623')}
            >
              <FileText size={16} />
              Lebenslauf öffnen
            </a>
          ) : (
            <span style={{
              fontSize: 12, color: '#9ca3af',
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontStyle: 'italic',
            }}>
              <FileText size={13} />
              Kein Lebenslauf hinterlegt
            </span>
          )}
        </div>
      </div>
    </div>
  )
}

/* ── Overlay-Hilfskomponenten ── */

function ContactAction({ icon, label, detail, href, primary }: {
  icon: ReactNode; label: string; detail: string; href?: string; primary?: boolean
}) {
  const disabled = !href
  const baseStyle: React.CSSProperties = {
    flex: 1, minWidth: 220,
    display: 'flex', alignItems: 'center', gap: 12,
    padding: '12px 16px', borderRadius: 14,
    textDecoration: 'none',
    border: primary ? 'none' : '1.5px solid #e5e7eb',
    background: disabled ? '#f9fafb' : (primary ? 'linear-gradient(135deg, #009B8D, #006B5F)' : 'white'),
    color: disabled ? '#9ca3af' : (primary ? 'white' : '#0B2623'),
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: 'all .15s',
    boxShadow: primary && !disabled ? '0 6px 18px rgba(0,155,141,0.28)' : 'none',
  }
  const inner = (
    <>
      <div style={{
        width: 40, height: 40, borderRadius: 12, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: disabled ? '#f3f4f6' : (primary ? 'rgba(255,255,255,0.18)' : '#f0fdfb'),
        color: disabled ? '#d1d5db' : (primary ? 'white' : '#009B8D'),
      }}>
        {icon}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: primary ? 0.85 : 0.6 }}>
          {label}
        </div>
        <div style={{
          fontSize: 13, fontWeight: 600, marginTop: 2,
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {detail}
        </div>
      </div>
    </>
  )
  if (disabled) return <div style={baseStyle}>{inner}</div>
  return <a href={href} style={baseStyle}>{inner}</a>
}

function Section({ title, icon, children }: { title: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <div style={{ padding: '22px 36px', borderBottom: '1px solid #f5f5f5' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
        fontSize: 11, fontWeight: 700, color: '#009B8D',
        letterSpacing: '0.18em', textTransform: 'uppercase',
      }}>
        {icon}
        {title}
      </div>
      {children}
    </div>
  )
}

function ProfileGrid({ children }: { children: ReactNode }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>{children}</div>
}

function ProfileItem({ label, value, icon }: { label: string; value?: string | null; icon?: ReactNode }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      padding: '10px 12px', borderRadius: 10,
      background: '#fafbfa', border: '1px solid #f0f0f0',
    }}>
      <div style={{
        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: 'white', color: '#009B8D', border: '1px solid #e5e7eb',
      }}>
        {icon}
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{
          fontSize: 10, fontWeight: 700, color: '#9ca3af',
          letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 2,
        }}>
          {label}
        </div>
        <div style={{ fontSize: 13, fontWeight: 600, color: value ? '#0B2623' : '#d1d5db' }}>
          {value || '–'}
        </div>
      </div>
    </div>
  )
}

function DetailNote({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{
      padding: '12px 14px', borderRadius: 10,
      background: '#fafbfa', border: '1px solid #f0f0f0',
    }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: '#9ca3af',
        letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4,
      }}>
        {label}
      </div>
      <div style={{ fontSize: 13, color: '#374151', lineHeight: 1.55, whiteSpace: 'pre-wrap' }}>
        {children}
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Matches
// ═══════════════════════════════════════════════════════════════

type FilterOp = 'contains' | 'equals' | 'startsWith' | 'notContains' | 'gt' | 'lt' | 'gte' | 'lte'
type ColFilter = { op: FilterOp; value: string }
type MatchFilters = Record<string, ColFilter>

const TEXT_FILTER_OPS: { value: FilterOp; label: string }[] = [
  { value: 'contains', label: 'enthält' },
  { value: 'equals', label: 'ist gleich' },
  { value: 'startsWith', label: 'beginnt mit' },
  { value: 'notContains', label: 'enthält nicht' },
]

const NUM_FILTER_OPS: { value: FilterOp; label: string }[] = [
  { value: 'equals', label: 'ist gleich' },
  { value: 'gt', label: 'größer als' },
  { value: 'gte', label: 'größer oder gleich' },
  { value: 'lt', label: 'kleiner als' },
  { value: 'lte', label: 'kleiner oder gleich' },
]

function applyColFilter(val: string, f: ColFilter, numeric?: boolean): boolean {
  const q = f.value.trim()
  if (!q) return true

  if (numeric || ['gt', 'lt', 'gte', 'lte'].includes(f.op)) {
    const numVal = parseFloat((val || '').replace(/[^0-9.,\-]/g, '').replace(',', '.'))
    const numQ = parseFloat(q.replace(',', '.'))
    if (isNaN(numVal) || isNaN(numQ)) {
      // Fallback text
      if (f.op === 'equals') return (val || '').toLowerCase() === q.toLowerCase()
      return true
    }
    switch (f.op) {
      case 'gt': return numVal > numQ
      case 'gte': return numVal >= numQ
      case 'lt': return numVal < numQ
      case 'lte': return numVal <= numQ
      case 'equals': return numVal === numQ
      default: return true
    }
  }

  const v = (val || '').toLowerCase()
  const ql = q.toLowerCase()
  switch (f.op) {
    case 'contains': return v.includes(ql)
    case 'equals': return v === ql
    case 'startsWith': return v.startsWith(ql)
    case 'notContains': return !v.includes(ql)
    default: return true
  }
}

function MatchesView() {
  const [allMatches, setAllMatches] = useState<Match[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [filters, setFilters] = useState<MatchFilters>({})
  const [activeFilterCol, setActiveFilterCol] = useState<string | null>(null)
  const [sortCol, setSortCol] = useState<string | null>(null)
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc')
  const pageSize = 25

  useEffect(() => {
    request<any>('/dashboard/matches?page=1&pageSize=9999')
      .then(r => { setAllMatches(r.data || []); setLoading(false) })
      .catch(() => setLoading(false))
  }, [])

  const cols: { key: string; label: string; accessor: (m: Match) => string; numeric?: boolean }[] = [
    { key: 'name',        label: 'Bewerber',    accessor: m => `${m.lead.firstName || ''} ${m.lead.lastName || ''}`.trim() },
    { key: 'email',       label: 'E-Mail',      accessor: m => m.lead.email || '' },
    { key: 'studiengang', label: 'Studiengang',  accessor: m => m.lead.studiengang || '' },
    { key: 'standort',    label: 'Standort',     accessor: m => m.lead.standort || '' },
    { key: 'suchprofil',  label: 'Suchprofil',   accessor: m => m.searchProfile.name || '' },
    { key: 'score',       label: 'Score',        accessor: m => `${Math.round((m.score || 0) * 100)}`, numeric: true },
    { key: 'status',      label: 'Status',       accessor: m => m.status },
    { key: 'datum',       label: 'Datum',        accessor: m => fmtDate(m.matchedAt) },
  ]

  const filtered = useMemo(() => {
    const active = Object.entries(filters).filter(([, f]) => f.value.trim())
    if (active.length === 0) return allMatches
    return allMatches.filter(m =>
      active.every(([key, f]) => {
        const col = cols.find(c => c.key === key)
        return col ? applyColFilter(col.accessor(m), f, col.numeric) : true
      })
    )
  }, [allMatches, filters])

  const sorted = useMemo(() => {
    if (!sortCol) return filtered
    const col = cols.find(c => c.key === sortCol)
    if (!col) return filtered
    return [...filtered].sort((a, b) => {
      const va = col.accessor(a)
      const vb = col.accessor(b)
      if (col.numeric) {
        const na = parseFloat(va.replace(/[^0-9.,\-]/g, '').replace(',', '.')) || 0
        const nb = parseFloat(vb.replace(/[^0-9.,\-]/g, '').replace(',', '.')) || 0
        return sortDir === 'asc' ? na - nb : nb - na
      }
      return sortDir === 'asc' ? va.localeCompare(vb, 'de') : vb.localeCompare(va, 'de')
    })
  }, [filtered, sortCol, sortDir])

  const totalPages = Math.ceil(sorted.length / pageSize)
  const pageData = sorted.slice((page - 1) * pageSize, page * pageSize)
  const activeFilterCount = Object.values(filters).filter(f => f.value.trim()).length

  const toggleSort = (key: string) => {
    if (sortCol === key) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc')
    } else {
      setSortCol(key)
      setSortDir('asc')
    }
    setPage(1)
  }

  const setFilter = (key: string, f: ColFilter) => {
    setFilters(prev => ({ ...prev, [key]: f }))
    setPage(1)
  }
  const clearFilter = (key: string) => {
    setFilters(prev => { const n = { ...prev }; delete n[key]; return n })
    setPage(1)
  }
  const clearAllFilters = () => { setFilters({}); setPage(1) }

  if (loading) return <Loading />

  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '36px 56px 56px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <h1 style={{ ...h1Style, marginBottom: 0 }}>
          Alle Matches
          <span style={{ fontSize: 18, fontWeight: 500, color: '#9ca3af', marginLeft: 8 }}>
            ({filtered.length}{activeFilterCount > 0 ? ` von ${allMatches.length}` : ''})
          </span>
        </h1>
        {activeFilterCount > 0 && (
          <PillBtn onClick={clearAllFilters} danger>
            {activeFilterCount} Filter aktiv — zurücksetzen
          </PillBtn>
        )}
      </div>

      <Card>
        {filtered.length === 0 ? (
          <div style={{ padding: 60, textAlign: 'center', color: '#999', fontSize: 15 }}>
            {activeFilterCount > 0 ? 'Keine Matches für diese Filter.' : 'Noch keine Matches vorhanden.'}
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#f9fafb' }}>
                    {cols.map(col => {
                      const hasFilter = filters[col.key]?.value?.trim()
                      const isSorted = sortCol === col.key
                      return (
                        <th key={col.key} style={{ ...thStyle, position: 'relative', cursor: 'pointer', userSelect: 'none' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            {/* Spaltenname → Klick sortiert */}
                            <span
                              onClick={() => toggleSort(col.key)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              title={`Sortieren nach ${col.label}`}
                            >
                              {col.label}
                              <span style={{
                                fontSize: 10, color: isSorted ? '#009B8D' : '#d1d5db',
                                transition: 'color .15s', lineHeight: 1,
                              }}>
                                {isSorted ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
                              </span>
                            </span>
                            {/* Filter-Icon → Klick öffnet Dropdown */}
                            <span
                              onClick={(e) => { e.stopPropagation(); setActiveFilterCol(activeFilterCol === col.key ? null : col.key) }}
                              style={{
                                width: 18, height: 18, borderRadius: 4, fontSize: 9, fontWeight: 800,
                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                background: hasFilter ? '#009B8D' : '#f0f0f0',
                                color: hasFilter ? 'white' : '#9ca3af',
                                transition: 'all .15s', cursor: 'pointer',
                              }}
                              title={`Filter: ${col.label}`}
                            >
                              ▼
                            </span>
                          </div>
                          {activeFilterCol === col.key && (
                            <ColumnFilterDropdown
                              colKey={col.key}
                              numeric={col.numeric}
                              current={filters[col.key]}
                              onApply={(f) => { setFilter(col.key, f); setActiveFilterCol(null) }}
                              onClear={() => { clearFilter(col.key); setActiveFilterCol(null) }}
                              onClose={() => setActiveFilterCol(null)}
                            />
                          )}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {pageData.map(m => (
                    <tr key={m.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={tdStyle}><strong>{m.lead.firstName} {m.lead.lastName}</strong></td>
                      <td style={tdStyle}>{m.lead.email || '–'}</td>
                      <td style={tdStyle}>{m.lead.studiengang || '–'}</td>
                      <td style={tdStyle}>{m.lead.standort || '–'}</td>
                      <td style={tdStyle}><Tag>{m.searchProfile.name}</Tag></td>
                      <td style={tdStyle}>
                        <span style={{
                          fontWeight: 700,
                          color: m.score >= 0.8 ? '#22c55e' : m.score >= 0.5 ? '#f59e0b' : '#ef4444',
                        }}>
                          {Math.round(m.score * 100)}%
                        </span>
                      </td>
                      <td style={tdStyle}><StatusBadge status={m.status} /></td>
                      <td style={tdStyle}>{fmtDate(m.matchedAt)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && <Pagination page={page} totalPages={totalPages} onChange={setPage} />}
          </>
        )}
      </Card>
    </div>
  )
}

/* ── Spaltenfilter-Dropdown ─────────────────────────────────────── */

function ColumnFilterDropdown({ colKey, numeric, current, onApply, onClear, onClose }: {
  colKey: string
  numeric?: boolean
  current?: ColFilter
  onApply: (f: ColFilter) => void
  onClear: () => void
  onClose: () => void
}) {
  const ops = numeric ? NUM_FILTER_OPS : TEXT_FILTER_OPS
  const defaultOp = numeric ? 'gte' : 'contains'
  const [op, setOp] = useState<FilterOp>(current?.op || defaultOp)
  const [value, setValue] = useState(current?.value || '')

  // Click-outside schließt
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (!t.closest(`[data-filter-col="${colKey}"]`)) onClose()
    }
    setTimeout(() => document.addEventListener('click', handler), 0)
    return () => document.removeEventListener('click', handler)
  }, [colKey, onClose])

  return (
    <div
      data-filter-col={colKey}
      onClick={(e) => e.stopPropagation()}
      style={{
        position: 'absolute', top: '100%', left: 0, zIndex: 50,
        background: 'white', borderRadius: 14,
        boxShadow: '0 12px 40px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.05)',
        padding: '16px 18px', minWidth: 260,
        animation: 'dm-pop-in .18s cubic-bezier(.2,.9,.3,1.2)',
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: '#0B2623', marginBottom: 10 }}>
        Filter: {colKey}
      </div>

      <select
        value={op}
        onChange={(e) => setOp(e.target.value as ColFilter['op'])}
        style={{
          width: '100%', padding: '8px 12px', borderRadius: 8,
          border: '1px solid #e5e7eb', fontSize: 13, marginBottom: 8,
          outline: 'none', background: 'white',
        }}
      >
        {ops.map(o => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>

      <input
        autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && value.trim()) onApply({ op, value }) }}
        placeholder={numeric ? 'z.B. 80' : 'Filterwert...'}
        style={{
          width: '100%', padding: '8px 12px', borderRadius: 8,
          border: '1px solid #e5e7eb', fontSize: 13, outline: 'none',
          boxSizing: 'border-box',
        }}
      />

      <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
        {current?.value && (
          <button onClick={onClear} style={{
            padding: '6px 14px', borderRadius: 8, border: '1px solid #e5e7eb',
            background: 'white', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#dc2626',
          }}>
            Entfernen
          </button>
        )}
        <button
          onClick={() => { if (value.trim()) onApply({ op, value }) }}
          disabled={!value.trim()}
          style={{
            padding: '6px 14px', borderRadius: 8, border: 'none',
            background: value.trim() ? '#009B8D' : '#e5e7eb',
            color: value.trim() ? 'white' : '#9ca3af',
            fontSize: 12, fontWeight: 700, cursor: value.trim() ? 'pointer' : 'not-allowed',
          }}
        >
          Anwenden
        </button>
      </div>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Benachrichtigungen
// ═══════════════════════════════════════════════════════════════

function NotificationsView() {
  const [notifs, setNotifs] = useState<Notification[]>([]); const [loading, setLoading] = useState(true)
  useEffect(() => { request<any>('/dashboard/notifications').then(r => { setNotifs(r.data || []); setLoading(false) }).catch(() => setLoading(false)) }, [])
  if (loading) return <Loading />
  return (
    <div style={{ maxWidth: 1280, margin: '0 auto', padding: '36px 56px 56px' }}>
      <h1 style={h1Style}>Benachrichtigungen</h1>
      <Card>{notifs.length === 0 ? <div style={{ padding: 60, textAlign: 'center', color: '#999', fontSize: 15 }}>Noch keine Benachrichtigungen.</div> : (
        <table style={tableStyle}><thead><tr style={{ background: '#f9fafb' }}><th style={thStyle}>Betreff</th><th style={thStyle}>Empfänger</th><th style={thStyle}>Kanal</th><th style={thStyle}>Status</th><th style={thStyle}>Gesendet</th><th style={thStyle}>Fehler</th></tr></thead>
        <tbody>{notifs.map(n => (
          <tr key={n.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
            <td style={tdStyle}>{n.subject}</td><td style={tdStyle}>{n.recipient}</td><td style={tdStyle}>{n.channel}</td>
            <td style={tdStyle}><span style={{ padding: '4px 12px', borderRadius: 14, fontSize: 12, fontWeight: 600, background: n.status === 'SENT' ? '#e6f4ea' : n.status === 'FAILED' ? '#FEE2E2' : '#fef7e0', color: n.status === 'SENT' ? '#137333' : n.status === 'FAILED' ? '#dc2626' : '#ea8600' }}>{n.status === 'SENT' ? 'Gesendet' : n.status === 'FAILED' ? 'Fehlgeschlagen' : 'Ausstehend'}</span></td>
            <td style={tdStyle}>{n.sentAt ? new Date(n.sentAt).toLocaleString('de-DE') : '–'}</td>
            <td style={{ ...tdStyle, color: '#dc2626', fontSize: 13 }}>{n.error || '–'}</td>
          </tr>
        ))}</tbody></table>
      )}</Card>
    </div>
  )
}

// ═══════════════════════════════════════════════════════════════
// Shared
// ═══════════════════════════════════════════════════════════════

function Loading() { return <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>Laden...</div> }
function Card({ children, borderLeft }: { children: ReactNode; borderLeft?: string }) { return <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e5e7eb', borderLeft: borderLeft ? `4px solid ${borderLeft}` : undefined, overflow: 'hidden' }}>{children}</div> }
function Tag({ children, color = 'teal' }: { children: ReactNode; color?: 'teal' | 'gray' }) { const bg = color === 'teal' ? '#e6f7f5' : '#f9fafb'; const fg = color === 'teal' ? '#006B5F' : '#999'; return <span style={{ background: bg, color: fg, padding: '4px 12px', borderRadius: 14, fontSize: 12, fontWeight: 600 }}>{children}</span> }
function StatusBadge({ status }: { status: string }) { const m: Record<string, { bg: string; color: string; label: string }> = { NEW: { bg: '#e6f7f5', color: '#006B5F', label: 'Neu' }, NOTIFIED: { bg: '#e6f4ea', color: '#137333', label: 'Benachrichtigt' }, VIEWED: { bg: '#fef7e0', color: '#ea8600', label: 'Angesehen' }, ARCHIVED: { bg: '#f9fafb', color: '#999', label: 'Archiviert' } }; const c = m[status] || m.NEW; return <span style={{ background: c.bg, color: c.color, padding: '4px 12px', borderRadius: 14, fontSize: 12, fontWeight: 600 }}>{c.label}</span> }
function PillBtn({ children, onClick, teal, danger }: { children: ReactNode; onClick?: () => void; teal?: boolean; danger?: boolean }) { return <button onClick={onClick} type="button" style={{ padding: '8px 20px', background: 'white', border: `1.5px solid ${danger ? '#dc2626' : teal ? '#009B8D' : '#e5e7eb'}`, borderRadius: 20, fontSize: 13, fontWeight: 700, cursor: 'pointer', color: danger ? '#dc2626' : teal ? '#009B8D' : '#374151', transition: 'all .15s' }}>{children}</button> }
function FieldRow({ label, value }: { label: string; value?: string | null }) { return <div style={{ display: 'flex', gap: 8 }}><span style={{ color: '#999', minWidth: 130, flexShrink: 0, fontSize: 13 }}>{label}</span><span style={{ fontSize: 14, fontWeight: value ? 500 : 400 }}>{value || '–'}</span></div> }

/* ── LeadAvatar (wiederverwendbar: Card + Overlay) ── */
function LeadAvatar({ lead, size = 48 }: { lead: Lead; size?: number }) {
  return (
    <Avatar
      photoUrl={lead.hasFiles ? `/api/dualmatch/leads/${lead.id}/photo` : null}
      fallbackName={`${lead.firstName ?? ''} ${lead.lastName ?? ''}`.trim()}
      size={size}
    />
  )
}
function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) { return <div style={{ display: 'flex', justifyContent: 'center', gap: 8, padding: 20 }}><button disabled={page <= 1} onClick={() => onChange(page - 1)} style={pageBtnStyle}>Zurück</button><span style={{ padding: '7px 16px', fontSize: 14, color: '#999' }}>Seite {page} von {totalPages}</span><button disabled={page >= totalPages} onClick={() => onChange(page + 1)} style={pageBtnStyle}>Weiter</button></div> }

const h1Style: React.CSSProperties = { fontSize: 28, fontWeight: 700, marginBottom: 28, letterSpacing: '-0.3px' }
const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse' }
const thStyle: React.CSSProperties = { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '0.5px' }
const tdStyle: React.CSSProperties = { padding: '14px 16px', fontSize: 14 }
const btnPrimary: React.CSSProperties = { padding: '10px 24px', background: '#009B8D', color: 'white', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 600, cursor: 'pointer' }
const inputStyle: React.CSSProperties = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid #e5e7eb', fontSize: 14, outline: 'none', boxSizing: 'border-box' }
const labelStyle: React.CSSProperties = { display: 'block', marginBottom: 6, fontSize: 14, fontWeight: 500 }
const pillSelectStyle: React.CSSProperties = { padding: '14px 20px', borderRadius: 999, border: '1px solid #e0e0e0', fontSize: 15, background: 'white', cursor: 'pointer', width: '100%', appearance: 'none', WebkitAppearance: 'none', backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' viewBox=\'0 0 24 24\' fill=\'none\' stroke=\'%23999\' stroke-width=\'2\'%3E%3Cpath d=\'M6 9l6 6 6-6\'/%3E%3C/svg%3E")', backgroundRepeat: 'no-repeat', backgroundPosition: 'right 16px center', paddingRight: 40 }
const pageBtnStyle: React.CSSProperties = { padding: '7px 16px', border: '1.5px solid #e5e7eb', borderRadius: 8, background: 'white', cursor: 'pointer', fontSize: 13, fontWeight: 500 }
function fmtDate(iso?: string) { if (!iso) return '–'; try { return new Date(iso).toLocaleDateString('de-DE') } catch { return iso } }
